import React, { useEffect, useRef, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import 'components/ui/collapse.scss';

// Animated collapse on the host element directly (no inner wrapper) so the
// DOM structure is preserved (e.g. <ul> -> <li>). Animates between 0 and the
// measured scrollHeight, then snaps to auto so the content stays responsive.
const Collapse = forwardRef(({ isOpen = false, children, tag: Tag = 'div', className, ...rest }, forwardedRef) => {
  const innerRef = useRef(null);
  const setRef = (node) => {
    innerRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  const [height, setHeight] = useState(isOpen ? null : 0); // null === 'auto'

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return undefined;
    const measured = el.scrollHeight;
    if (isOpen) {
      setHeight(measured);
      const t = setTimeout(() => setHeight(null), 260);
      return () => clearTimeout(t);
    }
    setHeight(measured);
    const r = requestAnimationFrame(() => setHeight(0));
    return () => cancelAnimationFrame(r);
  }, [isOpen]);

  const styleHeight = height === null ? 'auto' : `${height}px`;

  return (
    <Tag
      ref={setRef}
      className={classNames('ui-collapse', { 'ui-collapse--open': isOpen }, className)}
      style={{
        height: styleHeight,
        overflow: 'hidden',
        transition: 'height 0.25s ease',
      }}
      aria-hidden={!isOpen}
      {...rest}
    >
      {children}
    </Tag>
  );
});

Collapse.propTypes = {
  isOpen: PropTypes.bool,
  children: PropTypes.node,
  tag: PropTypes.elementType,
  className: PropTypes.string,
};

Collapse.displayName = 'Collapse';

export default Collapse;
